import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useReducer,
	useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	AUTH_MODES,
	buildAuthModeSearchParams,
	resolveAuthModeFromLocation,
} from '../../utils/authModeUrl';

export { AUTH_MODES };

// Auth actions
export const AUTH_ACTIONS = {
	SET_MODE: 'SET_MODE',
	SET_LOADING: 'SET_LOADING',
	SET_ERROR: 'SET_ERROR',
	CLEAR_ERROR: 'CLEAR_ERROR',
	RESET: 'RESET',
};

// Initial state
const initialState = {
	mode: AUTH_MODES.SIGN_IN,
	loading: false,
	error: null,
	user: null,
};

// Auth reducer
function authReducer(state, action) {
	switch (action.type) {
		case AUTH_ACTIONS.SET_MODE:
			return {
				...state,
				mode: action.payload,
				error: null, // Clear error when switching modes
			};
		case AUTH_ACTIONS.SET_LOADING:
			return {
				...state,
				loading: action.payload,
			};
		case AUTH_ACTIONS.SET_ERROR:
			return {
				...state,
				error: action.payload,
				loading: false,
			};
		case AUTH_ACTIONS.CLEAR_ERROR:
			return {
				...state,
				error: null,
			};
		case AUTH_ACTIONS.RESET:
			return initialState;
		default:
			return state;
	}
}

// Create context
const AuthFlowContext = createContext();

// Auth flow provider
export const AuthFlowProvider = ({
	children,
	initialMode = AUTH_MODES.SIGN_IN,
	/** When true, mode switches write `?mode=` and the URL drives the panel. */
	syncUrl = false,
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const skipNextUrlSync = useRef(false);

	const resolvedInitial = syncUrl
		? resolveAuthModeFromLocation({
				pathname: location.pathname,
				search: location.search,
			})
		: initialMode;

	const [state, dispatch] = useReducer(authReducer, {
		...initialState,
		mode: resolvedInitial,
	});

	// Browser back/forward or external ?mode= changes
	useEffect(() => {
		if (!syncUrl) return;
		if (skipNextUrlSync.current) {
			skipNextUrlSync.current = false;
			return;
		}
		const fromUrl = resolveAuthModeFromLocation({
			pathname: location.pathname,
			search: location.search,
		});
		if (fromUrl !== state.mode) {
			dispatch({ type: AUTH_ACTIONS.SET_MODE, payload: fromUrl });
		}
	}, [syncUrl, location.pathname, location.search, state.mode]);

	const setMode = useCallback(
		(mode) => {
			dispatch({ type: AUTH_ACTIONS.SET_MODE, payload: mode });
			if (!syncUrl) return;

			const nextParams = buildAuthModeSearchParams({
				pathname: location.pathname,
				currentSearch: location.search,
				mode,
			});
			const nextSearch = nextParams.toString();
			const current = location.search.startsWith('?')
				? location.search.slice(1)
				: location.search;
			if (nextSearch === current) return;

			skipNextUrlSync.current = true;
			navigate(
				{
					pathname: location.pathname,
					search: nextSearch ? `?${nextSearch}` : '',
				},
				{ replace: true },
			);
		},
		[syncUrl, location.pathname, location.search, navigate],
	);

	const setLoading = (loading) => {
		dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: loading });
	};

	const setError = (error) => {
		dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error });
	};

	const clearError = () => {
		dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
	};

	const reset = () => {
		dispatch({ type: AUTH_ACTIONS.RESET });
	};

	const value = {
		...state,
		setMode,
		setLoading,
		setError,
		clearError,
		reset,
	};

	return (
		<AuthFlowContext.Provider value={value}>{children}</AuthFlowContext.Provider>
	);
};

// Custom hook to use auth flow
export const useAuthFlow = () => {
	const context = useContext(AuthFlowContext);
	if (!context) {
		throw new Error('useAuthFlow must be used within an AuthFlowProvider');
	}
	return context;
};
